'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

interface FAQItem {
  q: string;
  a: string;
}

interface Category {
  id: string;
  icon: string;
  label: string;
  faqs: FAQItem[];
}

const categories: Category[] = [
  {
    id: 'getting-started',
    icon: '🚀',
    label: 'Getting Started',
    faqs: [
      {
        q: 'What is Uyir?',
        a: 'Uyir is an AI-powered emotional companion — a safe space to express how you\'re feeling, check in with your mood, and reflect on your day. Think of it like texting a close friend who\'s always there, never judges, and actually listens.',
      },
      {
        q: 'How do I create an account?',
        a: 'Tap "Get Started" on the home page and sign up with your email or Google account. It takes under a minute. No credit card required.',
      },
      {
        q: 'Is Uyir free to use?',
        a: 'Yes, Uyir\'s core features — mood check-ins, AI chat, and journaling — are free. We may introduce optional premium features in the future, but we\'ll always keep the essentials free.',
      },
      {
        q: 'What languages does Uyir support?',
        a: 'Uyir currently supports English, Hindi, Tamil, Kannada, and Telugu. You can set your preferred language in Profile → Language Preference. The AI companion adapts its tone accordingly.',
      },
      {
        q: 'Can I use Uyir on my phone?',
        a: 'Absolutely. Uyir is mobile-first and works great in your phone\'s browser. You can also add it to your home screen as a PWA (Progressive Web App) for an app-like experience — no app store needed.',
      },
    ],
  },
  {
    id: 'ai-companion',
    icon: '🤖',
    label: 'AI Companion',
    faqs: [
      {
        q: 'How does the AI companion work?',
        a: 'Uyir\'s AI is powered by OpenAI\'s GPT models. It\'s trained to respond in a warm, casual, non-judgmental way — more like a friend than a chatbot. It asks follow-up questions, remembers the context of your conversation, and adapts to how you\'re feeling.',
      },
      {
        q: 'Is the AI a therapist?',
        a: 'No. Uyir is not a mental health treatment or substitute for professional therapy. The AI companion is designed for everyday emotional expression and reflection. If you\'re going through something serious, please reach out to a qualified professional.',
      },
      {
        q: 'Does the AI remember past conversations?',
        a: 'Within a session, the AI remembers the full context of your conversation. Across sessions, it currently starts fresh — but we\'re working on long-term memory features.',
      },
      {
        q: 'Can I opt out of AI features?',
        a: 'Yes. Go to Settings → Privacy Controls and toggle off AI features. This disables AI chat responses and voice transcription. Your existing journal entries remain unaffected.',
      },
      {
        q: 'Why did the AI give a weird response?',
        a: 'AI isn\'t perfect — it can occasionally misread tone or give an off response. If something feels wrong, you can start a new conversation or tap the refresh icon. If you\'re concerned about a specific response, please report it via the contact form.',
      },
    ],
  },
  {
    id: 'privacy-security',
    icon: '🔒',
    label: 'Privacy & Security',
    faqs: [
      {
        q: 'Who can read my journal entries?',
        a: 'Nobody but you. Journal entries are encrypted using AES-GCM encryption before being stored — meaning even our team cannot read your content. Your emotional data is yours alone.',
      },
      {
        q: 'Does Uyir share my data with anyone?',
        a: 'We share data only with the service providers needed to run Uyir: OpenAI (for AI responses), Supabase (for storage), and Resend (for emails). We never sell your data or share it for advertising. See our Privacy Policy for full details.',
      },
      {
        q: 'How long does OpenAI keep my data?',
        a: 'Data sent to OpenAI via our API is retained for a maximum of 30 days for abuse monitoring, then permanently deleted. It is not used to train OpenAI models.',
      },
      {
        q: 'Can I delete my account and data?',
        a: 'Yes. Go to Settings → Delete Account. All your personal data — entries, messages, profile — will be permanently deleted within 30 days. This action is irreversible.',
      },
      {
        q: 'How do I export my data?',
        a: 'Go to Settings → Data Export. You\'ll receive a JSON file containing all your journal entries, mood check-ins, and profile data. We\'re working on additional export formats.',
      },
    ],
  },
  {
    id: 'features',
    icon: '✨',
    label: 'Features & Usage',
    faqs: [
      {
        q: 'How does voice journaling work?',
        a: 'Tap the mic button on the home screen or journal page. Speak naturally — Uyir will transcribe your voice using OpenAI Whisper and send it as a message. The audio itself is not stored, only the text transcript.',
      },
      {
        q: 'What are Guided Sessions?',
        a: 'Guided Sessions are structured reflection exercises — like "5-Min Stress Release" or "Gratitude Deep Dive". They walk you through a series of prompts in a chat-style flow. Find them in the Journal tab under "Guided Sessions".',
      },
      {
        q: 'What is the Weekly Reflection?',
        a: 'Every Sunday (or after 7 entries), Uyir generates a personalised weekly summary — your most common mood, best day, toughest day, and a warm reflection. It appears as a card on the home screen.',
      },
      {
        q: 'What is the Discover page?',
        a: 'Discover features human-written content from psychologists, therapists, and counsellors — tips, short articles, and coping strategies. Categories include Managing Anxiety, Processing Grief, Burnout Recovery, and more. No AI-generated content here.',
      },
      {
        q: 'How does the mood streak work?',
        a: 'Your streak counts consecutive days you\'ve checked in or journaled. It resets if you miss a day. You can see your streak in your Profile.',
      },
    ],
  },
  {
    id: 'account-billing',
    icon: '👤',
    label: 'Account & Notifications',
    faqs: [
      {
        q: 'How do I change my name or avatar?',
        a: 'Go to the top-right menu → Profile. You can update your display name and upload a new avatar from there.',
      },
      {
        q: 'How do I reset my password?',
        a: 'On the login screen, tap "Forgot password?" and enter your email. You\'ll receive a reset link within a few minutes. Check your spam folder if it doesn\'t arrive.',
      },
      {
        q: 'How do I manage notification settings?',
        a: 'Go to the top-right menu → Notifications. You can toggle daily reminders, streak alerts, and weekly summary emails on or off.',
      },
      {
        q: 'I\'m not receiving emails from Uyir.',
        a: 'Check your spam or promotions folder. Add hello@introspectlabs.com to your contacts to ensure delivery. If the issue persists, contact us via the form below.',
      },
      {
        q: 'How do I contact support?',
        a: 'Use our contact form at /contact or email us at hello@introspectlabs.com. We typically respond within 24–48 hours.',
      },
    ],
  },
];

interface AccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ item, isOpen, onToggle }: AccordionItemProps) {
  const answerId = `faq-answer-${item.q.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`;
  const questionId = `faq-question-${item.q.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`;
  return (
    <div className="border-b last:border-b-0" style={{ borderColor: 'var(--glass-border)' }}>
      <button
        id={questionId}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
        className="w-full flex items-center justify-between gap-4 py-4 text-left group focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-sm"
      >
        <span className="text-sm md:text-base font-medium text-foreground group-hover:text-[var(--primary)] transition-colors">
          {item.q}
        </span>
        <span
          aria-hidden="true"
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
          style={{
            background: isOpen ? 'var(--primary)' : 'rgba(124, 111, 247, 0.15)',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div
          id={answerId}
          role="region"
          aria-labelledby={questionId}
          className="pb-4 text-sm text-muted-foreground leading-relaxed fade-in"
        >
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function HelpCenterPage() {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const currentCategory = categories.find((c) => c.id === activeCategory);

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-1/3 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'var(--primary)' }} />
        <div className="absolute bottom-1/3 left-0 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: 'var(--accent)' }} />
      </div>

      {/* Topbar */}
      <header className="glass-strong border-b border-[var(--glass-border)] sticky top-0 z-50">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg gradient-text">Uyir</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="relative max-w-screen-lg mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center space-y-3 pb-10">
          <h1 className="text-3xl md:text-4xl font-bold gradient-text">Help Center</h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
            Got questions? We've got answers. Can't find what you need?{' '}
            <Link href="/contact" className="underline" style={{ color: 'var(--primary)' }}>Drop us a message.</Link>
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
          {/* Category sidebar — horizontal scroll on mobile, sticky sidebar on md+ */}
          <aside className="md:w-56 flex-shrink-0">
            <div className="glass rounded-2xl p-2 sm:p-3 md:sticky md:top-24">
              {/* Mobile: horizontal scroll */}
              <div className="flex md:flex-col gap-1 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setOpenItems({});
                  }}
                  className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-left text-sm transition-all duration-150 whitespace-nowrap flex-shrink-0 md:flex-shrink md:whitespace-normal md:w-full"
                  style={{
                    background: activeCategory === cat.id ? 'rgba(124, 111, 247, 0.2)' : 'transparent',
                    color: activeCategory === cat.id ? 'var(--primary)' : 'var(--muted-foreground)',
                    fontWeight: activeCategory === cat.id ? 600 : 400,
                  }}
                >
                  <span className="text-base">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
              </div>
            </div>
          </aside>

          {/* FAQ accordion */}
          <div className="flex-1">
            {currentCategory && (
              <div className="glass rounded-2xl p-6 md:p-8 fade-in">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xl">{currentCategory.icon}</span>
                  <h2 className="text-xl font-bold text-foreground">{currentCategory.label}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(124, 111, 247, 0.15)', color: 'var(--primary)' }}>
                    {currentCategory.faqs.length} questions
                  </span>
                </div>
                <div>
                  {currentCategory.faqs.map((faq, idx) => {
                    const key = `${currentCategory.id}-${idx}`;
                    return (
                      <AccordionItem
                        key={key}
                        item={faq}
                        isOpen={!!openItems[key]}
                        onToggle={() => toggleItem(key)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Still need help */}
            <div className="mt-6 rounded-2xl p-6 text-center space-y-3 border" style={{ background: 'rgba(236, 72, 153, 0.06)', borderColor: 'rgba(236, 72, 153, 0.2)' }}>
              <p className="text-foreground font-semibold">Still need help?</p>
              <p className="text-muted-foreground text-sm">We're real humans and we actually read every message.</p>
              <Link
                href="/contact"
                className="inline-block px-6 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Footer nav */}
        <div className="flex flex-wrap justify-center gap-6 pt-10 text-sm text-muted-foreground">
          <Link href="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
        </div>
      </main>
    </div>
  );
}
